export type MapSizePreset = 'large' | 'small' | 'hidden';

export const SMALL_MAP_WIDTH_PX = 340;
export const MIN_MAP_WIDTH_PX = 300;
export const MIN_TABLE_WIDTH_PX = 400;
export const MAP_SIZE_SNAP_PX = 12;

// Absent width = Large. Derived, never stored, so an off-preset width highlights nothing.
export function mapSizePreset(hidden: boolean, widthPx: number | null | undefined, largePx: number): MapSizePreset | null {
  if (hidden) return 'hidden';
  if (widthPx == null) return 'large';
  if (Math.abs(widthPx - largePx) <= MAP_SIZE_SNAP_PX) return 'large';
  if (Math.abs(widthPx - SMALL_MAP_WIDTH_PX) <= MAP_SIZE_SNAP_PX) return 'small';
  return null;
}

// Map floor wins over the table's on a body too narrow for both.
export function clampMapWidth(widthPx: number, bodyWidthPx: number): number {
  return Math.max(Math.min(widthPx, bodyWidthPx - MIN_TABLE_WIDTH_PX), MIN_MAP_WIDTH_PX);
}

// Past this the divider collapses the map instead of sticking at the floor.
export const MAP_COLLAPSE_AT_PX = Math.round(MIN_MAP_WIDTH_PX * 0.62);
export const MAP_WIDTH_KEY_STEP_PX = 24;

export interface MapSizeCommit {
  hidden: boolean;
  widthPx: number | null;
}

// Released drag. A width inside a preset's snap band commits that preset's own
// width (null for Large) so the segment stays lit after the window resizes.
export function mapDragCommit(widthPx: number, bodyWidthPx: number): MapSizeCommit {
  if (widthPx < MAP_COLLAPSE_AT_PX) return { hidden: true, widthPx: null };
  const clamped = clampMapWidth(widthPx, bodyWidthPx);
  switch (mapSizePreset(false, clamped, bodyWidthPx / 2)) {
    case 'large': return { hidden: false, widthPx: null };
    case 'small': return { hidden: false, widthPx: SMALL_MAP_WIDTH_PX };
    default:      return { hidden: false, widthPx: clamped };
  }
}

export function mapDragPreview(widthPx: number, bodyWidthPx: number): number {
  return widthPx < MAP_COLLAPSE_AT_PX ? 0 : clampMapWidth(widthPx, bodyWidthPx);
}

export function mapWidthAfterNudge(
  hidden: boolean,
  widthPx: number | null,
  bodyWidthPx: number,
  deltaPx: number,
): MapSizeCommit {
  if (hidden) return deltaPx > 0 ? { hidden: false, widthPx: MIN_MAP_WIDTH_PX } : { hidden: true, widthPx: null };
  return mapDragCommit((widthPx ?? bodyWidthPx / 2) + deltaPx, bodyWidthPx);
}
